/**
 * B6, the upload offer: the banner that says what an audio upload costs before anything is sent,
 * and the upload itself once the visitor says yes.
 *
 * Every tool page has it (pages/tool.ts), and the auto subtitle generator (pages/autosub.ts) uses
 * the same one, so the limits, the refusals and the wording are B6's wherever audio is sent. What
 * happens after the server accepts the upload is the page's: a tool page goes to the job page, the
 * generator stays and waits for the transcript itself (`onSubmitted`).
 *
 * `banner` is B6_VIDEO's for a video's audio (lib/video-flow.ts): the same offer, upload and
 * refusals, with a banner that says the audio came out of a video and the video stays here.
 *
 * THE USAGE COUNTER (lib/usage.ts) hears one "loaded" when the server accepts a file for
 * transcription, under the page's slug, with the format of the file the visitor dropped: the
 * video's container when only its audio was sent (`source`), the audio's own format otherwise.
 * A refusal, a cancel or a file over the cap counts nothing.
 *
 * A PAGE THAT TRANSLATES (SEO_BRIEF_09 2.1) sends `translate_to` with the file, the target picker's
 * value read at the moment the upload is sent (`translateTo`), and counts `translate_start` when
 * the server takes it (lib/translate-target.ts): `file` for a dropped file, `record` for a
 * recording.
 */

import { ApiError, getQuota, submitUpload } from './api.ts';
import type { SubmitResponse } from './api-types.ts';
import { qs } from './dom.ts';
import { classify, extensionOf } from './dropzone.ts';
import { track } from './events.ts';
import { minutesCost, shortFilename } from './format.ts';
import { loadPlatforms } from './platforms.ts';
import { showLimitDialog } from './quota.ts';
import { renderState, type SlotValues, showProblem, showState } from './states.ts';
import { countTranslateStart } from './translate-target.ts';
import { audioDuration, capMegabytes, overCap, within } from './upload.ts';
import {
  countUse,
  mediaFormat,
  recordingFormat,
  type TranslateSource,
  type UsageFormat,
} from './usage.ts';

export interface OfferBanner {
  readonly state: string;
  readonly values: SlotValues;
}

export interface UploadOfferDeps {
  readonly region: HTMLElement;
  /** The page's slug in the usage counter. */
  readonly tool: string;
  /** The server took the upload. The response names the job, or the transcript when resolved. */
  readonly onSubmitted: (response: SubmitResponse) => void;
  /**
   * The spoken language the page declares, a Whisper code from its row (the language pages,
   * src/formats/language-tools.ts): sent with the upload so the model is told rather than
   * guessing. Absent on every other page, which sends no language at all.
   */
  readonly language?: string;
  /**
   * The speech's language as the page says it when the upload is sent, where it can change: the
   * pair's on a page that opens on one and swaps it (SEO_BRIEF_11, lib/translate-pair.ts
   * `pairSource`). Undefined, or absent, and `language` is sent as above.
   */
  readonly languageNow?: () => string | undefined;
  /**
   * The language the speech is to be translated into, read when the upload is sent: the target
   * picker's value on a page that translates (lib/translate-target.ts `translateTarget`).
   * Absent, or answering undefined, on every other page, which sends no such part.
   */
  readonly translateTo?: () => string | undefined;
  /**
   * The name of that target as the offer's sentence says it, in the page's language
   * (lib/translate-target.ts `translateTargetName`): B6's "we'll translate it into {language}".
   * Absent, or undefined, and the offer names the page's own target, as the server wrote it.
   */
  readonly translateName?: () => string | undefined;
}

export interface UploadOffer {
  /**
   * Show the offer for this file and send it if the visitor confirms. `source` is the video the
   * file's audio came out of, when it did: what the usage counter counts the upload as.
   */
  offer(file: File, banner?: OfferBanner, source?: File): Promise<void>;
  /** A newer drop arrived: a banner still waiting on its numbers is not painted. */
  supersede(): void;
  /**
   * Send a file the visitor has already asked to transcribe, with no banner first: a recording
   * from the microphone panel, whose Transcribe button is the confirmation (lib/mic-panel.ts).
   * The same cap, the same upload, the same refusals and the same `onSubmitted` as `offer`.
   * `counted` replaces the "loaded" count: the panel counts `record_transcribe` instead.
   * Resolves true once the server took it.
   */
  send(file: File, durationS: number | null, counted?: () => void): Promise<boolean>;
}

export function createUploadOffer(deps: UploadOfferDeps): UploadOffer {
  const { region } = deps;
  // Bumped by every drop and every cancel, so a slow read for an earlier file
  // cannot paint its banner over what the visitor is looking at now.
  let current = 0;

  async function offer(
    file: File,
    banner: OfferBanner = { state: 'B6', values: {} },
    source: File = file,
  ): Promise<void> {
    const mine = ++current;
    // In parallel, each bounded: the banner waits for the slowest of three
    // short reads and then shows whatever arrived (lib/upload.ts).
    const [platforms, quota, durationS] = await Promise.all([
      within(loadPlatforms(), 4000, null),
      within(getQuota(), 4000, null),
      audioDuration(file),
    ]);
    if (mine !== current) return;

    const cap = platforms?.upload_max_bytes;
    const limitMb = capMegabytes(cap);
    if (overCap(file.size, cap)) {
      // Refused here, before a byte is sent: past Cloudflare's 100 MB the app
      // never sees the request at all (see lib/upload.ts).
      showState(region, 'UPLOAD_TOO_LARGE', { limit_mb: limitMb });
      track('error_state', { state: 'UPLOAD_TOO_LARGE' });
      return;
    }

    const frag = renderState(banner.state, {
      filename: shortFilename(file.name),
      // null drops the number and keeps "some of your"; null on the budget
      // keeps the template's own default rather than writing a blank over it.
      minutes: durationS === null ? null : String(minutesCost(durationS)),
      budget_minutes: quota ? String(Math.round(quota.asr_seconds_budget / 60)) : null,
      // The target the visitor picked, on a page that translates; null keeps the page's own.
      language: deps.translateName?.() ?? null,
      ...banner.values,
    });
    if (!frag) return;
    region.replaceChildren(frag);
    // Into view, and to the MIDDLE of it. The banner region sits under the
    // dropzone, which fills a phone's screen, so B6 opened below the fold and
    // behind the fixed bottom ad bar (live at 31f48ec, 375px), with nothing
    // to say a question was waiting. `nearest` would park it at the very edge
    // the bar covers; `center` cannot be under it.
    region.focus({ preventScroll: true });
    region.scrollIntoView({
      block: 'center',
      behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
    const confirm = qs<HTMLButtonElement>('[data-action="upload-transcribe"]', region);
    const cancel = qs<HTMLButtonElement>('[data-action="upload-cancel"]', region);
    cancel?.addEventListener('click', () => {
      current += 1;
      region.replaceChildren();
    });
    confirm?.addEventListener('click', () => {
      confirm.disabled = true;
      const kind = classify(source) === 'container' ? 'video' : 'audio';
      const format = mediaFormat(extensionOf(source.name), kind);
      void upload(file, durationS, limitMb, () => countUse('loaded', deps.tool, format), {
        format,
        source: 'file',
      });
    });
  }

  /** The upload and its refusals, B6's whichever way the visitor said yes. */
  function upload(
    file: File,
    durationS: number | null,
    limitMb: string | null,
    counted: () => void,
    what: { format: UsageFormat; source: TranslateSource },
  ): Promise<boolean> {
    const target = deps.translateTo?.();
    const language = deps.languageNow?.() ?? deps.language;
    return submitUpload(file, durationS, undefined, language, target)
      .then((response) => {
        counted();
        countTranslateStart(
          response.translate,
          deps.tool,
          what.format,
          what.source,
          response.resolved ? undefined : response.job_id,
        );
        deps.onSubmitted(response);
        return true;
      })
      .catch((err: unknown) => {
        if (!(err instanceof ApiError)) {
          showState(region, 'NETWORK', {});
          return false;
        }
        const problem = err.problem;
        // E10 is a dialog, not a banner, here as on the landing page.
        if (problem.code === 'QUOTA_EXHAUSTED') {
          region.replaceChildren();
          showLimitDialog({
            budgetSeconds: problem.quota?.asr_seconds_budget,
            resetsInS: problem.quota?.resets_in_s ?? null,
          });
          track('error_state', { state: 'E10' });
          return false;
        }
        // Every answer here is about a file, so E5 takes its upload wording.
        showProblem(region, problem, { limit_mb: limitMb }, { upload: true });
        return false;
      });
  }

  async function send(
    file: File,
    durationS: number | null,
    counted: () => void = () => undefined,
  ): Promise<boolean> {
    current += 1;
    region.replaceChildren();
    const platforms = await within(loadPlatforms(), 4000, null);
    const cap = platforms?.upload_max_bytes;
    const limitMb = capMegabytes(cap);
    if (overCap(file.size, cap)) {
      showState(region, 'UPLOAD_TOO_LARGE', { limit_mb: limitMb });
      track('error_state', { state: 'UPLOAD_TOO_LARGE' });
      return false;
    }
    // `send` is the microphone panel's: a recording, counted as one.
    return upload(file, durationS, limitMb, counted, {
      format: recordingFormat(extensionOf(file.name)),
      source: 'record',
    });
  }

  return {
    offer,
    supersede() {
      current += 1;
    },
    send,
  };
}
